import React, { useCallback } from 'react';
import { Plus, Trash2, Layers } from 'lucide-react';
import { useMergeFields } from '../../../hooks/useTemplates';

const OPERATORS = {
  string: [
    { value: 'eq', label: 'equals' },
    { value: 'neq', label: 'does not equal' },
    { value: 'in', label: 'is in' },
    { value: 'notIn', label: 'is not in' },
  ],
  number: [
    { value: 'eq', label: 'equals' },
    { value: 'neq', label: 'does not equal' },
    { value: 'gt', label: 'greater than' },
    { value: 'lt', label: 'less than' },
    { value: 'gte', label: 'greater or equal' },
    { value: 'lte', label: 'less or equal' },
  ],
  boolean: [
    { value: 'eq', label: 'equals' },
  ],
  // A fixed set of known values, so the value input becomes a dropdown.
  enum: [
    { value: 'eq', label: 'is' },
    { value: 'neq', label: 'is not' },
    { value: 'in', label: 'is any of' },
    { value: 'notIn', label: 'is none of' },
  ],
  // The FIELD holds a list (e.g. the groups a person belongs to) and the value
  // is one item, which is the mirror of how `in` works.
  list: [
    { value: 'contains', label: 'includes' },
    { value: 'notContains', label: 'does not include' },
  ],
};

/**
 * Fields may be a plain string (EmailRuleEditor) or a descriptor
 * { field, label, type, options } (CampaignEditor). Normalise to a descriptor.
 */
function toDescriptor(f) {
  return typeof f === 'string' ? { field: f, label: f } : f;
}

/** Find the descriptor for a selected field name across all groups. */
function findDescriptor(groups, fieldName) {
  for (const group of groups) {
    for (const f of group.fields) {
      const d = toDescriptor(f);
      if (d.field === fieldName) return d;
    }
  }
  return null;
}

function inferFieldType(fieldName) {
  if (!fieldName) return 'string';
  const lower = fieldName.toLowerCase();
  if (lower.endsWith('amount') || lower.endsWith('nights') ||
      lower.endsWith('id') || lower === 'policy.maxGuests') {
    return 'number';
  }
  if (lower.endsWith('refundable') || lower.includes('opt_out') || lower.includes('active')) {
    return 'boolean';
  }
  return 'string';
}

function ConditionRow({ condition, onChange, onRemove, groups }) {
  // A declared type always beats guessing from the field name.
  const descriptor = findDescriptor(groups, condition.field);
  const fieldType = descriptor?.type || inferFieldType(condition.field);
  const ops = OPERATORS[fieldType] || OPERATORS.string;

  const typeOf = (field) => {
    const d = findDescriptor(groups, field);
    return d?.type || inferFieldType(field);
  };

  const defaultValueFor = (type, d) => {
    if (type === 'number') return 0;
    if (type === 'boolean') return true;
    if (type === 'enum' || type === 'list') return d?.options?.[0]?.value ?? '';
    return '';
  };

  const handleFieldChange = (field) => {
    const newType = typeOf(field);
    const newDescriptor = findDescriptor(groups, field);
    const firstOp = (OPERATORS[newType] || OPERATORS.string)[0].value;
    const defaultValue = defaultValueFor(newType, newDescriptor);
    onChange({ ...condition, field, op: firstOp, value: defaultValue });
  };

  const handleOpChange = (op) => {
    onChange({ ...condition, op });
  };

  const coerceOption = (raw) => {
    // Group ids arrive from a <select> as strings; the stored condition must
    // keep the original type or `contains` will never match.
    const match = descriptor?.options?.find(o => String(o.value) === raw);
    return match ? match.value : raw;
  };

  const handleValueChange = (value) => {
    if (fieldType === 'number') {
      onChange({ ...condition, value: parseFloat(value) || 0 });
    } else if (fieldType === 'boolean') {
      onChange({ ...condition, value: value === 'true' });
    } else if (condition.op === 'in' || condition.op === 'notIn') {
      onChange({ ...condition, value: value.split(',').map((s) => s.trim()).filter(Boolean) });
    } else {
      onChange({ ...condition, value });
    }
  };

  const displayValue = condition.op === 'in' || condition.op === 'notIn'
    ? (Array.isArray(condition.value) ? condition.value.join(', ') : condition.value || '')
    : condition.value;

  return (
    <div className="cb-row">
      <select
        className="cb-field-select"
        value={condition.field || ''}
        onChange={(e) => handleFieldChange(e.target.value)}
      >
        <option value="">Select field...</option>
        {groups.map((group) => (
          <optgroup key={group.name} label={group.name}>
            {group.fields.map((f) => {
              const d = toDescriptor(f);
              return <option key={d.field} value={d.field}>{d.label || d.field}</option>;
            })}
          </optgroup>
        ))}
      </select>

      <select
        className="cb-op-select"
        value={condition.op || 'eq'}
        onChange={(e) => handleOpChange(e.target.value)}
      >
        {ops.map((op) => (
          <option key={op.value} value={op.value}>{op.label}</option>
        ))}
      </select>

      {fieldType === 'boolean' ? (
        <select
          className="cb-value-input"
          value={String(condition.value)}
          onChange={(e) => handleValueChange(e.target.value)}
        >
          <option value="true">yes</option>
          <option value="false">no</option>
        </select>
      ) : descriptor?.options && condition.op !== 'in' && condition.op !== 'notIn' ? (
        <select
          className="cb-value-input"
          value={String(condition.value ?? '')}
          onChange={(e) => onChange({ ...condition, value: coerceOption(e.target.value) })}
        >
          {descriptor.options.map((o) => (
            <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
          ))}
        </select>
      ) : (
        <input
          type={fieldType === 'number' ? 'number' : 'text'}
          className="cb-value-input"
          value={displayValue ?? ''}
          onChange={(e) => handleValueChange(e.target.value)}
          placeholder={
            condition.op === 'in' || condition.op === 'notIn'
              ? 'val1, val2, val3'
              : 'value'
          }
        />
      )}

      <button type="button" className="cb-remove-btn" onClick={onRemove} title="Remove condition">
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function ConditionGroup({ condition, onChange, onRemove, depth, groups }) {
  const isAnd = !!condition.and;
  const children = isAnd ? condition.and : condition.or;
  const groupType = isAnd ? 'and' : 'or';

  const toggleGroupType = () => {
    const newType = isAnd ? 'or' : 'and';
    onChange({ [newType]: children });
  };

  const updateChild = useCallback((index, newChild) => {
    const newChildren = [...children];
    newChildren[index] = newChild;
    onChange({ [groupType]: newChildren });
  }, [children, groupType, onChange]);

  const removeChild = useCallback((index) => {
    const newChildren = children.filter((_, i) => i !== index);
    if (newChildren.length === 0) {
      onRemove();
    } else {
      onChange({ [groupType]: newChildren });
    }
  }, [children, groupType, onChange, onRemove]);

  const addCondition = () => {
    onChange({ [groupType]: [...children, { field: '', op: 'eq', value: '' }] });
  };

  const addGroup = () => {
    if (depth >= 2) return; // Max nesting depth: 3 levels (0, 1, 2)
    const innerType = isAnd ? 'or' : 'and';
    onChange({
      [groupType]: [
        ...children,
        { [innerType]: [{ field: '', op: 'eq', value: '' }] },
      ],
    });
  };

  return (
    <div className={`cb-group cb-depth-${depth}`}>
      <div className="cb-group-header">
        <button type="button" className="cb-group-toggle" onClick={toggleGroupType}>
          {groupType.toUpperCase()}
        </button>
        <span className="cb-group-label">
          {isAnd ? 'All conditions must match' : 'Any condition can match'}
        </span>
        {depth > 0 && (
          <button type="button" className="cb-remove-btn" onClick={onRemove} title="Remove group">
            <Trash2 size={14} />
          </button>
        )}
      </div>

      <div className="cb-group-children">
        {children.map((child, index) => {
          const isGroup = child.and || child.or;
          return isGroup ? (
            <ConditionGroup
              key={index}
              condition={child}
              onChange={(newChild) => updateChild(index, newChild)}
              onRemove={() => removeChild(index)}
              depth={depth + 1}
              groups={groups}
            />
          ) : (
            <ConditionRow
              key={index}
              condition={child}
              onChange={(newChild) => updateChild(index, newChild)}
              onRemove={() => removeChild(index)}
              groups={groups}
            />
          );
        })}
      </div>

      <div className="cb-group-actions">
        <button type="button" className="cb-add-btn" onClick={addCondition}>
          <Plus size={14} /> Add Condition
        </button>
        {depth < 2 && (
          <button type="button" className="cb-add-btn" onClick={addGroup}>
            <Layers size={14} /> Add Group
          </button>
        )}
      </div>
    </div>
  );
}

function ConditionBuilder({ value, onChange }) {
  const { groups } = useMergeFields();

  if (!value || Object.keys(value).length === 0) {
    return (
      <div className="cb-empty">
        <p>No conditions — this rule will match all events.</p>
        <button
          type="button"
          className="btn btn-sm btn-secondary"
          onClick={() => onChange({ and: [{ field: '', op: 'eq', value: '' }] })}
        >
          <Plus size={14} /> Add Condition
        </button>
      </div>
    );
  }

  // If the value is a leaf node, wrap it in an AND group
  const normalized = (value.and || value.or) ? value : { and: [value] };

  return (
    <ConditionGroup
      condition={normalized}
      onChange={onChange}
      onRemove={() => onChange(null)}
      depth={0}
      groups={groups}
    />
  );
}

export default ConditionBuilder;
